import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { navigateToUrl } from "single-spa";
import classNames from "classnames";
import { useSearchParams } from "react-router-dom";
import { useCreateInitiative } from "@gx1/gx-npm-buysmart";
import { Loader, ProductLogo, SnackbarBanner, TooltipV2 } from "@gx1/gx-npm-ui";
import { PrimaryButtonComponent, TertiaryButtonComponent, TypographyComponent } from "@gx1/gx-ui-common";
import { getAsyncRequest, useCaptureEventsV2, useUserState } from "@gx1/gx-npm-lib";
import { ClientEvent } from "../../app.constants";
import styles from "./onboarding.styles.module.scss";

const OnboardingComponent = () => {
  const { t } = useTranslation();

  const captureEvents = useCaptureEventsV2();
  const { createInitiative, isLoadingInitiative } = useCreateInitiative();
  const { canStartEvaluation } = useUserState();
  const [searchParams] = useSearchParams();
  const [productName, setProductName] = useState("");

  const [productImageLoc, setProductImageLoc] = useState("");
  const [templateName, setTemplateName] = useState("");
  const [loading, setLoading] = useState(false);
  const [errorInitiative, setErrorInitiative] = useState(false);

  const pid = searchParams.get("pid") || "";
  const tid = searchParams.get("tid") || "";

  useEffect(() => {
    const gxtmSource = searchParams.get("gxtm_source") || "";
    const gxtmType = searchParams.get("gxtm_type") || "";
    const gxtmPage = searchParams.get("gxtm_page") || "";
    captureEvents([{
      eventType: ClientEvent.HOME_ONBOARDING_PAGE_VIEWED,
      metaData: { gxtmSource, gxtmType, gxtmPage }
    }]);
  }, [captureEvents, searchParams]);

  useEffect(() => {
    if (!tid) {
      navigateToUrl("/s/home");
      return;
    }
    (async () => {
      setLoading(true);
      try {
        const response = await getAsyncRequest(
          `/api/v2/data/user-home/onboarding?templateId=${tid}${pid ? `&productId=${pid}` : ""}`
        );
        if (response.status !== 200 || !response.data?.data) {
          throw new Error();
        }
        setProductName(response.data.data.productName);
        setProductImageLoc(response.data.data.productImageLoc);
        setTemplateName(response.data.data.templateName);
      } catch (err) {
        navigateToUrl("/s/home");
      }
      setLoading(false);
    })();
  }, [pid, tid, setLoading]);

  const handleGetStarted = async () => {
    try {
      await createInitiative(Number(tid));
    } catch (err) {
      setErrorInitiative(true);
    }
  };

  const handleClose = () => {
    navigateToUrl("/s/home");
  };

  return (
    <>
      <div aria-label={t("onboarding")} className={styles.container}>
        <div className={styles.contentContainer}>
          <div className={styles.headingContainer}>
            <TypographyComponent boldness="regular" rootClassName={styles.headingElement} styling={"h2-xl"}>
              {t("Welcome to Your BuySmart Evaluation")}
            </TypographyComponent>
          </div>
          <TypographyComponent rootClassName={classNames(styles.descriptionElement)} styling="p-default">
            {t(
              "A BuySmart evaluation will allow you to manage all of your evaluation activities involving this market in one convenient location."
            )}
          </TypographyComponent>
          <div className={styles.divider} />
          {loading && <Loader size={100} />}
          {!loading && templateName && (
            <>
              <div className={classNames(styles.marketInterestContainer, !productName && styles.noProductInterest)}>
                <TypographyComponent rootClassName={styles.marketInterest} styling="p-sm">
                  {t("Market of Interest")}
                </TypographyComponent>
                <TypographyComponent
                  boldness="semi"
                  element="h6"
                  rootClassName={styles.marketInterestName}
                  styling="title-header-sm"
                >
                  {templateName}
                </TypographyComponent>
              </div>
              {productName &&
                <div className={styles.vendorInterestContainer}>
                  <TypographyComponent rootClassName={styles.vendorInterest} styling="p-sm">
                    {t("Vendor of Interest")}
                  </TypographyComponent>
                  <div className={styles.productContainer}>
                    <ProductLogo
                      imageHeight="40"
                      imageWidth="40"
                      logo={productImageLoc}
                      name={productName}
                      rootClassName={classNames(styles.productLogo)}
                    />
                    <TypographyComponent
                      boldness="semi"
                      element="h6"
                      rootClassName={styles.productName}
                      styling="title-header-sm"
                    >
                      {productName}
                    </TypographyComponent>
                  </div>
                </div>
              }
              <div className={styles.actionContainer}>
                <TooltipV2
                  deactivate={canStartEvaluation}
                  title={t("Your account does not have permission to create evaluations. Please contact your Gartner Account Manager for assistance.")}
                  placement="top"
                  enterDelay={1500}
                  enterNextDelay={1500}
                  PopperProps={{
                    modifiers: [
                      { name: "offset", options: { offset: [0, 2] } },
                    ]
                  }}
                >
                  <div>
                    <PrimaryButtonComponent
                      isDisabled={!canStartEvaluation}
                      isLoading={isLoadingInitiative}
                      onClick={handleGetStarted}
                      rootClassName={classNames(styles.btnGetStarted)}
                    >
                      {t("Let's Get Started")}
                    </PrimaryButtonComponent>
                  </div>
                </TooltipV2>
                <TertiaryButtonComponent isDisabled={isLoadingInitiative} onClick={handleClose}>
                  {t("I'm Not Ready Yet")}
                </TertiaryButtonComponent>
              </div>
            </>
          )}
        </div>
      </div>
      <SnackbarBanner
        isDefaultErrorMessage={true}
        isOpen={errorInitiative}
        setIsOpen={() => setErrorInitiative(false)}
        type="ERROR"
      />
    </>
  );
};

export default OnboardingComponent;
